import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormikProps } from 'formik';
import { isFunction, isNull, map } from 'lodash';
import React, { useCallback } from 'react';

import {
  IModal,
  IModalFooterProps,
  IModalImagePreview,
  IModalProperties,
} from './modal-renderer';
import { Button } from '../button/button';
import { FormValues } from '../form/form';
import {
  CardHeader,
  DialogActions,
  DialogContent,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';

/** ModalInner only handles standard modal types — imagePreview is rendered directly in ModalRenderer. */
type IModalStandard<TForm extends FormValues<TForm>> = Exclude<
  IModal<TForm>,
  IModalImagePreview
>;

interface IModalInnerPropsBase<TForm extends FormValues<TForm>> {
  modal: IModalStandard<TForm>;
  modalProps: IModalProperties;
  formikProps?: FormikProps<TForm>;
}

interface IModalInnerPropsSave<TForm extends FormValues<TForm>>
  extends IModalInnerPropsBase<TForm> {
  formikProps: FormikProps<TForm>;
}

export type IModalInnerProps<TForm extends FormValues<TForm>> =
  | IModalInnerPropsBase<TForm>
  | IModalInnerPropsSave<TForm>;

export default function ModalInner<TForm extends FormValues<TForm>>({
  modal,
  modalProps,
  formikProps,
}: IModalInnerProps<TForm>) {
  const renderModalBody = useCallback(() => {
    if (isFunction(modal.body)) {
      if (
        (formikProps && modal.type === 'save') ||
        (formikProps && modal.type === 'readOnly')
      ) {
        return modal.body(formikProps);
      }
      if (modal.type === 'info') {
        return modal.body(modalProps);
      }

      throw new Error(`Modal type doesn't support a function body.`);
    }
    return modal.body;
  }, [formikProps, modal, modalProps]);

  const renderModalFooter = useCallback(() => {
    if (isFunction(modal.footer)) {
      // Cast footer to a known signature to resolve TypeScript's intersection
      // type inference across the modal union — safe because ModalInner is only
      // ever called with standard (non-imagePreview) modal types.
      const footerFn = modal.footer as (
        props: IModalFooterProps<TForm>,
      ) => React.ReactNode;
      return footerFn({ formikProps, modalProps });
    }
    return modal.footer;
  }, [formikProps, modal, modalProps]);

  return (
    <>
      {!modalProps.hideHeader && (
        <CardHeader
          title={modal.title}
          avatar={!!modal.icon && <FontAwesomeIcon icon={modal.icon} />}
          action={
            <IconButton onClick={modalProps.onClose}>
              <Close />
            </IconButton>
          }
        />
      )}

      <DialogContent dividers autoFocus>
        {renderModalBody()}
      </DialogContent>
      <DialogActions>
        {modal.footer
          ? renderModalFooter()
          : !isNull(modal.footer) && (
              <>
                {map(modalProps.buttons, (button, idx) => (
                  <Button key={idx} {...button} />
                ))}
              </>
            )}
      </DialogActions>
    </>
  );
}
